import styled from '@emotion/styled'
import { type FunctionComponent, useEffect, useState } from 'react'
import { IconButton } from '@instacart/ids-customers'
import { ConfirmIcon } from '@instacart/ids-core'
import { startCase } from 'lodash'
import { spacing } from '@retailer-platform/shared-components'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import {
  AttributeManagementNextStepTypeEnum,
  type PipelineStep,
} from '../../../types/attributeManagement.types.ts'
import { PipelineFormStepControlFork } from './PipelineFormStepControlFork.tsx'
import { PipelineFormStepControlRegex } from './PipelineFormStepControlRegex.tsx'
import { PipelineFormStepControlNumericRanges } from './PipelineFormStepControlNumericRanges.tsx'
import { PipelineFormStepControlFieldMatch } from './PipelineFormStepControlFieldMatch.tsx'
import { PipelineFormStepControlHalt } from './PipelineFormStepControlHalt.tsx'

const ActionsRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
})

const StyledTable = styled.table({
  width: '100%',
  borderCollapse: 'collapse',
})

const StyledTh = styled.th({
  textAlign: 'left',
  borderBottom: '1px solid #e0e0e0',
  fontWeight: 600,
  lineHeight: 1.5,
  padding: `${spacing.X24}`,
  paddingLeft: `${spacing.X12}`,
  paddingRight: `${spacing.X12}`,
})

const StyledTd = styled.td({
  padding: `${spacing.X20}`,
  paddingLeft: `${spacing.X12}`,
  paddingRight: `${spacing.X12}`,
  verticalAlign: 'middle',
  borderBottom: '1px solid #e0e0e0',
  lineHeight: 1.5,
})

const StyledTr = styled.tr({
  '&:hover': {
    backgroundColor: '#f9f9f9',
  },
})

interface Props {
  step: PipelineStep
  onChange: (step: PipelineStep) => void
}

export const PipelineFormStepSelectControlModal: FunctionComponent<Props> = ({
  step,
  onChange,
}) => {
  const [selectedControlType, setSelectedControlType] =
    useState<AttributeManagementNextStepTypeEnum | null>(null)
  const i18n = useDomainMessages({
    id: 'catalogAdminDomain.attributeManagement.table.headers.id',
    description: 'catalogAdminDomain.attributeManagement.table.headers.description',
    name: 'catalogAdminDomain.attributeManagement.table.headers.description',
    type: 'catalogAdminDomain.attributeManagement.table.headers.type',
    typeHelp: 'catalogAdminDomain.attributeManagement.table.headers.type.help',
    owner: 'catalogAdminDomain.attributeManagement.table.headers.owner',
    actions: 'catalogAdminDomain.attributeManagement.table.headers.actions',
    select:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.select',
    regexDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.regexDescription',
    haltDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.haltDescription',
    forkDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.forkDescription',
    rangeDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.rangeDescription',
    fieldMatchDescription:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.stepSelectModal.fieldMatchDescription',
  })

  useEffect(() => {
    if (step?.nextStepsRule?.type) {
      setSelectedControlType(step?.nextStepsRule?.type)
    }
  }, [step])

  const listData: {
    type: AttributeManagementNextStepTypeEnum
    description: string
  }[] = [
    {
      type: AttributeManagementNextStepTypeEnum.RegexRules,
      description: i18n.regexDescription,
    },
    {
      type: AttributeManagementNextStepTypeEnum.Halt,
      description: i18n.haltDescription,
    },
    {
      type: AttributeManagementNextStepTypeEnum.Fork,
      description: i18n.forkDescription,
    },
    {
      type: AttributeManagementNextStepTypeEnum.NumericRange,
      description: i18n.rangeDescription,
    },
    {
      type: AttributeManagementNextStepTypeEnum.FieldMatch,
      description: i18n.fieldMatchDescription,
    },
  ]

  const columns = [
    {
      id: 'name',
      Cell: ({ row }: { row: { original: (typeof listData)[0] } }) => (
        <div style={{ whiteSpace: 'nowrap' }}>{startCase(row.original.type)}</div>
      ),
      Header: i18n.name,
      width: '150px',
    },
    {
      id: 'description',
      Cell: ({ row }: { row: { original: (typeof listData)[0] } }) => (
        <>{row.original.description}</>
      ),
      Header: i18n.description,
      width: 'auto',
    },
    {
      id: 'actions',
      Cell: ({ row }: { row: { original: (typeof listData)[0] } }) => (
        <ActionsRow>
          <IconButton
            icon={ConfirmIcon}
            accessibleLabel={''}
            onPress={() => setSelectedControlType(row.original.type)}
          />
        </ActionsRow>
      ),
      width: 'auto',
      Header: i18n.actions,
    },
  ]

  if (selectedControlType === AttributeManagementNextStepTypeEnum.Fork) {
    return <PipelineFormStepControlFork step={step} onChange={onChange} />
  } else if (selectedControlType === AttributeManagementNextStepTypeEnum.RegexRules) {
    return <PipelineFormStepControlRegex step={step} onChange={onChange} />
  } else if (selectedControlType === AttributeManagementNextStepTypeEnum.NumericRange) {
    return <PipelineFormStepControlNumericRanges step={step} onChange={onChange} />
  } else if (selectedControlType === AttributeManagementNextStepTypeEnum.FieldMatch) {
    return <PipelineFormStepControlFieldMatch step={step} onChange={onChange} />
  } else if (selectedControlType === AttributeManagementNextStepTypeEnum.Halt) {
    return <PipelineFormStepControlHalt step={step} onChange={onChange} />
  }

  return (
    <StyledTable>
      <thead>
        <tr>
          {columns.map(column => (
            <StyledTh key={column.id} style={{ width: column.width }}>
              {column.Header}
            </StyledTh>
          ))}
        </tr>
      </thead>
      <tbody>
        {listData.map((item, rowIndex) => (
          <StyledTr key={rowIndex}>
            {columns.map(column => (
              <StyledTd
                key={column.id}
                style={rowIndex == listData.length - 1 ? { borderBottom: 0 } : undefined}
              >
                {column.Cell({ row: { original: item } })}
              </StyledTd>
            ))}
          </StyledTr>
        ))}
      </tbody>
    </StyledTable>
  )
}
